import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { subscribeToEvents } from "../services/eventsService";
import { subscribeToGallery } from "../services/galleryService";

const toDate = (value) => {
  if (!value) {
    return null;
  }
  if (value.toDate) {
    return value.toDate();
  }
  return new Date(value);
};

const formatDate = (date) => {
  if (!date) {
    return "";
  }
  return new Intl.DateTimeFormat("hi-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const LandingPage = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  useEffect(() => {
    const unsubscribeGallery = subscribeToGallery((items) => {
      const visibleItems = items.filter((item) => item.visible !== false);
      const featured = visibleItems.filter((item) => item.featured);
      setCarouselImages(featured.length > 0 ? featured : visibleItems);
    });

    const unsubscribeEvents = subscribeToEvents((items) => {
      const today = new Date();
      const upcoming = items
        .map((event) => ({ ...event, eventDate: toDate(event.eventDate) }))
        .filter((event) => !event.eventDate || event.eventDate >= today)
        .slice(0, 3);
      setUpcomingEvents(upcoming);
    });

    return () => {
      if (unsubscribeGallery) {
        unsubscribeGallery();
      }
      if (unsubscribeEvents) {
        unsubscribeEvents();
      }
    };
  }, []);

  useEffect(() => {
    if (!carouselImages.length) {
      return undefined;
    }
    const interval = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselImages.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [carouselImages]);

  const missionHighlights = useMemo(
    () => [
      {
        titleHindi: "वेदों का प्रकाश",
        titleEnglish: "Illuminating the Vedas",
        descriptionHindi:
          "वेदों के सन्देश को प्रत्येक परिवार तक पहुँचाने का संकल्प।",
        descriptionEnglish:
          "Committed to sharing the timeless wisdom of the Vedas with every family.",
      },
      {
        titleHindi: "समाज सेवा",
        titleEnglish: "Service to Society",
        descriptionHindi:
          "शिक्षा, स्वास्थ्य, और संस्कृति के माध्यम से समाज उन्नति का अभियान।",
        descriptionEnglish:
          "Advancing society through education, healthcare, and cultural initiatives.",
      },
      {
        titleHindi: "समानता और नैतिकता",
        titleEnglish: "Equality and Ethics",
        descriptionHindi: "मानवता, समानता और नैतिक जीवन शैली का प्रचार।",
        descriptionEnglish:
          "Promoting humanity, equality, and a righteous way of life.",
      },
    ],
    [],
  );

  return (
    <div className="landing-page">
      <section className="hero-section">
        {carouselImages.length > 0 ? (
          <div className="carousel">
            {carouselImages.map((image, index) => (
              <figure
                key={image.id}
                className={`carousel-slide ${index === activeSlide ? "active" : ""}`}
              >
                <img
                  src={image.downloadUrl}
                  alt={image.titleEnglish || "Arya Samaj"}
                />
                {(image.titleHindi || image.titleEnglish) && (
                  <figcaption>
                    <span>{image.titleHindi}</span>
                    <span className="caption">{image.titleEnglish}</span>
                  </figcaption>
                )}
              </figure>
            ))}
            <div className="carousel-dots">
              {carouselImages.map((image, index) => (
                <button
                  key={image.id}
                  type="button"
                  className={index === activeSlide ? "active" : ""}
                  onClick={() => setActiveSlide(index)}
                  aria-label={`Slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="carousel empty">
            <p>आध्यात्मिक चित्र शीघ्र ही जोड़े जाएंगे…</p>
            <span className="caption">Spiritual visuals coming soon…</span>
          </div>
        )}
        <div className="hero-text">
          <h1>आर्य समाज अबोहर</h1>
          <p>धर्म, शिक्षा और सेवा का समन्वय</p>
          <span className="caption">
            Arya Samaj Abohar · Harmony of Dharma, Education, and Service
          </span>
          <Link className="primary-link" to="/events">
            आगामी आयोजन देखें
            <span className="caption">View Upcoming Events</span>
          </Link>
        </div>
      </section>

      <section className="mission-section">
        <h2>हमारी दिशा</h2>
        <span className="caption">Our Guiding Path</span>
        <div className="mission-cards">
          {missionHighlights.map((item) => (
            <article key={item.titleEnglish}>
              <h3>{item.titleHindi}</h3>
              <span className="caption">{item.titleEnglish}</span>
              <p>{item.descriptionHindi}</p>
              <span className="caption">{item.descriptionEnglish}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="events-preview">
        <div className="section-header">
          <h2>आगामी आयोजन</h2>
          <span className="caption">Upcoming Events</span>
          <Link to="/events" className="secondary-link">
            सभी देखें
            <span className="caption">View All</span>
          </Link>
        </div>
        {upcomingEvents.length > 0 ? (
          <div className="event-cards">
            {upcomingEvents.map((event) => (
              <article key={event.id}>
                <h3>{event.titleHindi}</h3>
                <span className="caption">{event.titleEnglish}</span>
                <p>{event.descriptionHindi}</p>
                <span className="caption">{event.descriptionEnglish}</span>
                <p className="event-date">{formatDate(event.eventDate)}</p>
              </article>
            ))}
          </div>
        ) : (
          <p>नए आयोजन शीघ्र ही घोषित किए जाएंगे।</p>
        )}
      </section>

      <section className="tenets-section">
        <h2>आर्यसमाज के दस नियम</h2>
        <span className="caption">10 Principles of Arya Samaj</span>
        <ul>
          <li>
            <span>
              सब सत्यविद्या और जो पदार्थ विद्या से जाने जाते हैं , उन सबका आदि
              मूल परमेश्वर है ।
            </span>
            <span className="caption">
              God is the efficient cause of all true knowledge and all that is
              known through knowledge.
            </span>
          </li>
          <li>
            <span>
              ईश्वर सच्चिदान्नदस्वरुप , निराकार , सर्वशक्तिमान , न्यायकारी ,
              दयालु , अजन्मा , अनन्त , निर्विकार , अनादि , अनुपम , सर्वाधार,
              सर्वेश्वर , सर्वव्यापक , सर्वान्तर्यामी , अजर , अभय , नित्य,
              पवित्र और सृष्टिकर्ता है । उसी की उपासना करनी योग्य है ।
            </span>
            <span className="caption">
              God is existent, intelligent and blissful. He is formless,
              omniscient, just, merciful, unborn, endless, unchangeable,
              beginning-less, unequaled, the support of all, the master of all,
              omnipresent, immanent, un-aging, immortal, fearless, eternal and
              holy, and the maker of all. He alone is worthy of being worshiped.
            </span>
          </li>
          <li>
            <span>
              वेद सब सत्यविद्याओं का पुस्तक है। वेद का पढ़ना-पढ़ाना और
              सुनना-सुनाना सब आर्यो का परम धर्म है।
            </span>
            <span className="caption">
              The Veda’s are the scriptures of all true knowledge. It is the
              paramount duty of all Arya’s to read them, teach them, recite
              them, and to hear them being read.
            </span>
          </li>
          <li>
            <span>
              सत्य के ग्रहण करने और असत्य के छोड़ने में सर्वदा उद्यत रहना चाहिये
              ।
            </span>
            <span className="caption">
              One should always be ready to accept truth and to renounce
              untruth.
            </span>
          </li>
          <li>
            <span>
              सब काम धर्मानुसार अर्थात् सत्य और असत्य को विचार करके करने चाहिये।
            </span>
            <span className="caption">
              All acts should be performed in accordance with Dharma that is,
              after deliberating what is right and wrong.
            </span>
          </li>
          <li>
            <span>
              संसार का उपकार करना इस समाज का मुख्य उद्देश्य है अर्थात शारीरिक,
              आत्मिक और सामाजिक उन्नति करना ।
            </span>
            <span className="caption">
              The prime object of the Arya Samaj is to do good to the world,
              that is, to promote physical, spiritual and social good of
              everyone.
            </span>
          </li>
          <li>
            <span>सब से प्रीतिपूर्वक धर्मानुसार यथायोग्य वर्तना चाहिये ।</span>
            <span className="caption">
              Our conduct towards all should be guided by love, righteousness
              and justice.
            </span>
          </li>
          <li>
            <span>अविद्या का नाश और विद्या की वृद्धि करनी चाहिये ।</span>
            <span className="caption">
              We should dispel Avidya (ignorance) and promote Vidya (knowledge).
            </span>
          </li>
          <li>
            <span>
              प्रत्येक को अपनी ही उन्नति से सन्तुष्ट न रहना चाहिये किन्तु सब की
              उन्नति में अपनी उन्नति समझनी चाहिये ।
            </span>
            <span className="caption">
              No one should be content with promoting his/her good only; on the
              contrary, one should look for his/her good in promoting the good
              of all.
            </span>
          </li>
          <li>
            <span>
              सब मनुष्यों को सामाजिक सर्वहितकारी नियम पालने में परतन्त्र रहना
              चाहिये और प्रत्येक हितकारी नियम में सब स्वतन्त्र रहें ।
            </span>
            <span className="caption">
              One should regard oneself under restriction to follow the rules of
              society calculated to promote the well being of all, while in
              following the rules of individual welfare all should be free.
            </span>
          </li>
        </ul>
      </section>
    </div>
  );
};

export default LandingPage;
